/**
 * BrandsList — Lista de marcas del workspace activo.
 * Permite crear, editar, archivar marcas.
 * Click en marca → navega a /v2/brands/:id
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import WorkspaceSwitcher from '@/components/v2/WorkspaceSwitcher';
import { useWorkspacesV2 } from '@/contexts/WorkspacesV2Context';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { brandsApi } from '@/components/v2/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Building2, Plus, MoreVertical, Pencil, Archive, ArchiveRestore, Globe, Briefcase, FileText,
} from 'lucide-react';
import { toast } from 'sonner';


export default function BrandsList() {
  const navigate = useNavigate();
  const { 
    activeWorkspace, brands, loading, isAdmin, refreshBrands,
  } = useWorkspacesV2();
  const { workspaces: legacyWorkspaces, switchWorkspace } = useWorkspace();

  const [showCreate, setShowCreate] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedBrands, setArchivedBrands] = useState([]);

  // Form state
  const [form, setForm] = useState({
    name: '', industry: '', website: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditingBrand(null);
    setForm({ name: '', industry: '', website: '', notes: '' });
    setShowCreate(true);
  };

  const openEdit = (brand) => {
    setEditingBrand(brand);
    setForm({
      name: brand.name || '',
      industry: brand.industry || '',
      website: brand.website || '',
      notes: brand.notes || '',
    });
    setShowCreate(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingBrand) {
        await brandsApi.update(editingBrand.id, form);
        toast.success(`"${form.name}" actualizada`);
      } else {
        await brandsApi.create(activeWorkspace.id, form);
        toast.success(`"${form.name}" creada`);
      }
      setShowCreate(false);
      await refreshBrands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando marca');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (brand) => {
    if (!window.confirm(`¿Archivar "${brand.name}"? La data histórica se conserva.`)) return;
    try {
      await brandsApi.archive(brand.id);
      toast.success(`"${brand.name}" archivada`);
      await refreshBrands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error archivando');
    }
  };

  const handleRestore = async (brand) => {
    try {
      await brandsApi.restore(brand.id);
      toast.success(`"${brand.name}" restaurada`);
      await refreshBrands();
      if (showArchived) loadArchived();
    } catch (e) {
      toast.error('Error restaurando');
    }
  };

  const loadArchived = async () => {
    try {
      const all = await brandsApi.list(activeWorkspace.id, true);
      setArchivedBrands(all.filter(b => !b.is_active));
    } catch (e) {
      console.error(e);
    }
  };

  const toggleArchived = async () => {
    if (!showArchived) await loadArchived();
    setShowArchived(!showArchived);
  };

  const displayBrands = showArchived ? archivedBrands : brands;

  // ============ RENDER ============

  if (!activeWorkspace && !loading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="p-8 text-center bg-slate-900 border-slate-800 text-slate-100">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h2 className="text-lg font-semibold mb-1">No hay workspace activo</h2>
            <p className="text-sm text-muted-foreground">Crea un workspace para empezar</p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <WorkspaceSwitcher />
            </div>
            <h1 className="text-2xl font-bold">Marcas</h1>
            <p className="text-sm text-muted-foreground">
              {showArchived ? 'Marcas archivadas' : `${brands.length} marca${brands.length !== 1 ? 's' : ''} activa${brands.length !== 1 ? 's' : ''}`}
              {activeWorkspace && ` · Límite del plan: ${activeWorkspace.max_clients}`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={toggleArchived}>
              {showArchived ? <Building2 className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              {showArchived ? 'Ver activas' : 'Ver archivadas'}
            </Button>
            {isAdmin && !showArchived && (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva marca
              </Button>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}
          </div>
        )}

        {/* Empty state */}
        {!loading && displayBrands.length === 0 && (
          <Card className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-semibold mb-1">
              {showArchived ? 'No hay marcas archivadas' : 'No hay marcas todavía'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {showArchived ? 'Las marcas archivadas aparecerán aquí' : 'Crea tu primera marca para conectar cuentas'}
            </p>
            {isAdmin && !showArchived && (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Crear marca
              </Button>
            )}
          </Card>
        )}

        {/* Brands grid */}
        {!loading && displayBrands.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayBrands.map(brand => (
              <Card 
                key={brand.id} 
                className="p-5 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all cursor-pointer relative group rounded-lg"
                onClick={() => {
                  if (showArchived) return;
                  // Sincronizar con el contexto viejo (que usa el Dashboard / Analytics / etc.)
                  const effectiveId = brand.legacy_client_id || brand.id;
                  const legacyMatch = legacyWorkspaces.find(w => w.id === effectiveId);
                  if (legacyMatch) switchWorkspace(legacyMatch);
                  navigate(`/v2/brands/${brand.id}`);
                }}
              >
                {/* Header con menu */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate text-slate-900">{brand.name}</h3>
                      {brand.industry && (
                        <p className="text-xs text-slate-500 truncate">{brand.industry}</p>
                      )}
                    </div>
                  </div>

                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                        {!showArchived && (
                          <>
                            <DropdownMenuItem onSelect={() => openEdit(brand)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleArchive(brand)} className="text-orange-600">
                              <Archive className="h-4 w-4 mr-2" />
                              Archivar
                            </DropdownMenuItem>
                          </>
                        )}
                        {showArchived && (
                          <DropdownMenuItem onSelect={() => handleRestore(brand)} className="text-green-600">
                            <ArchiveRestore className="h-4 w-4 mr-2" />
                            Restaurar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Body */}
                <div className="space-y-2 text-sm">
                  {brand.website && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Globe className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{brand.website}</span>
                    </div>
                  )}
                  {brand.notes && (
                    <p className="text-xs text-slate-400 line-clamp-2">{brand.notes}</p>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs">
                  {brand.legacy_client_id ? (
                    <Badge variant="secondary" className="text-[10px]">Migrada</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Nueva v2</Badge>
                  )}
                  <span className="text-slate-400">
                    {new Date(brand.created_at).toLocaleDateString('es-CL')}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBrand ? `Editar "${editingBrand.name}"` : 'Nueva marca'}
            </DialogTitle>
            <DialogDescription>
              {editingBrand 
                ? 'Modifica los datos de la marca.'
                : `Crea una nueva marca en "${activeWorkspace?.name}".`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" /> Nombre *
              </label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Oncovix, Tu Casa Fácil"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5" /> Industria
              </label>
              <Input
                value={form.industry}
                onChange={e => setForm({ ...form, industry: e.target.value })}
                placeholder="Ej: Salud, E-commerce, SaaS"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" /> Sitio web
              </label>
              <Input
                value={form.website}
                onChange={e => setForm({ ...form, website: e.target.value })}
                placeholder="https://ejemplo.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> Notas
              </label>
              <Input
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Notas internas (opcional)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? 'Guardando...' : (editingBrand ? 'Guardar' : 'Crear marca')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
